import React, { useState, useEffect } from 'react';
import DivTag from '../Components/DivComponent/DivTag';
import ReportHeading from '../Components/ReportHeadings/ReportHeadings';
import { RetrievePayouts, UpdateTxn, ReponseStatusTxn } from '../../services/PayoutService/payoutSerice';
import { motion } from 'framer-motion';
import 'jspdf-autotable'; // Import AutoTable plugin
import Seo from '../../components/SeoComponent/Seo';
import moment from 'moment';
import { IoMdDownload } from "react-icons/io";
import DataModal from '../Components/ConfirmPopUp/DataModal';
import PaginationData from '../Components/PaginationData';
import { showErrorToast, showSuccessToast } from '../../utils/toastUtils';
import html2pdf from 'html2pdf.js';

const PayoutReport = () => {
  const [payouts, setPayouts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [pagination, setPagination] = useState();
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [useFiltersFlag, setUseFiltersFlag] = useState(false);
  const [isModalOpens, setIsModalOpens] = useState(false);
  const [txnResponse, setTxnResponse] = useState(null);

  const [filters, setFilters] = useState({
    total_data: 10,
    account_number: "",
    beneficiary_name: "",
    amount: "",
    from_date: "",
    to_date: "",
    txn_type: "",
    user_id: ""
  });


  const handleTxnStatus = async (id) => {
    try {
      const res = await ReponseStatusTxn({ id });
      if (res?.data || res?.data?.data) {
        setTxnResponse(res);
        setIsModalOpens(true);
      } else {
        showErrorToast(res?.message || "Failed to fetch txn status");
      }
    } catch (err) {
      showErrorToast("Something went wrong while fetching txn status");
    }
  };


  const fetchPayouts = async (page = 1, useFilters = false) => {
    setLoading(true);

    let dataToSend = { total_data: filters.total_data };

    if (useFilters) {
      dataToSend = Object.fromEntries(
        Object.entries(filters).filter(
          ([, value]) =>
            value !== "" && value !== null && value !== undefined
        )
      );
    }

    const res = await RetrievePayouts(page, dataToSend);

    if (res?.data || res?.data?.data) {
      setPayouts(res.data);

      if (res.pagination) {
        setPagination(res.pagination);
        setCurrentPage(res.pagination.current_page || 1);
        setLastPage(res.pagination.last_page || 1);
      }
    } else {
      setPayouts([]);
    }

    setLoading(false);
  };

  // 🔹 Handle initial load + pagination
  useEffect(() => {
    fetchPayouts(currentPage, useFiltersFlag);
  }, [currentPage, useFiltersFlag]);

  // 🔹 Handle page click
  function handlePage(page) {
    setCurrentPage(page);
  }

  // 🔹 Handle search click
  function handleSearch() {
    setUseFiltersFlag(true);  // enable filter mode
    setCurrentPage(1);        // reset to page 1
  }


  function handleReset() {
    setFilters({
      total_data: 10,
      account_number: "",
      beneficiary_name: "",
      user_id: "",
      amount: 0,
      from_date: "",
      to_date: "",
      txn_type: ""
    });
    setUseFiltersFlag(false); // disable filter mode
    setCurrentPage(1);
  }

  const handleDownload = () => {
    const element = document.getElementById('txn-report-card');

    const opt = {
      margin: 0,
      filename: `txn_${selectedReport.transaction_id || 'report'}.pdf`,
      image: { type: 'jpeg', quality: 1 },
      html2canvas: {
        scale: 3,
        useCORS: true
      },
      jsPDF: {
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait'
      }
    };

    html2pdf().set(opt).from(element).save();
  };


  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1.2 }}
    >
      <DataModal visible={isOpen} onClose={() => setIsOpen(false)} className="!p-0">
        <div className="w-full max-w-7xl p-1 sm:p-6 md:p-2 relative bg-white rounded-lg shadow-lg">
          <div className="bg-white rounded-xl shadow-lg p-1 space-y-2 border" id="txn-report-card">
            <h2 className="text-blue-800 font-bold text-2xl border-b text-center">Report</h2>
            {selectedReport ? (
              <>
                <div className="bg-blue-50 rounded-md p-2 shadow-inner">
                  <h3 className="text-blue-700 font-semibold text-lg mb-1">Transaction Details</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                    <div>
                      <span className="text-gray-500 font-semibold">Transaction ID:</span>
                      <p>{selectedReport?.transaction_id || '-'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500 font-semibold">Reference ID:</span>
                      <p>{selectedReport?.payout_reference?.reference_id || '-'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500 font-semibold">Status:</span>
                      <span
                        className=
                        "inline-block px-1 py-1 ms-1 rounded-xl text-black font-semibold text-sm"
                      >
                        {selectedReport?.status?.value || '-'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 font-semibold">Date:</span>
                      <p>{moment(selectedReport?.created_at).format('DD/MM/YYYY hh:mm A')}</p>
                    </div>
                    <div>
                      <span className="text-gray-500 font-semibold">Amount:</span>
                      <p>₹{selectedReport?.amount || '-'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500 font-semibold">Factor:</span>
                      <p>{selectedReport?.factor || '-'}</p>
                    </div>
                  </div>
                </div>


                <div className="bg-green-50 rounded-md p-1 shadow-inner">
                  <h3 className="text-green-700 font-semibold text-lg mb-1">Beneficiary Details</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                    <div>
                      <span className="text-gray-500 font-semibold">Name:</span>
                      <p>{selectedReport?.beneficiary_name || '-'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500 font-semibold">Address:</span>
                      <p>{selectedReport?.beneficiary_address || '-'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500 font-semibold">Account Number:</span>
                      <p>{selectedReport?.credit_account_number}</p>
                    </div>
                    <div>
                      <span className="text-gray-500 font-semibold">IFSC Code:</span>
                      <p>{selectedReport?.ifsc_code}</p>
                    </div>
                    <div>
                      <span className="text-gray-500 font-semibold">Mode:</span>
                      <p>{selectedReport?.payout_mode?.name}</p>
                    </div>
                    <div>
                      <span className="text-gray-500 font-semibold">Method:</span>
                      <p>{selectedReport?.payout_method?.name}</p>
                    </div>
                  </div>
                </div>


                <div className="bg-yellow-50 rounded-md p-1 shadow-inner">
                  <h3 className="text-yellow-700 font-semibold text-lg mb-1">Initiator Info</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-500 font-semibold">Email:</span>
                      <p>{selectedReport?.email}</p>
                    </div>
                    <div>
                      <span className="text-gray-500 font-semibold">Phone:</span>
                      <p>{selectedReport?.phone}</p>
                    </div>
                  </div>
                </div>


                {selectedReport.failure_message && (
                  <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-1 rounded">
                    <p><strong>Failure Message:</strong> {selectedReport?.failure_message}</p>
                  </div>
                )}

                <div className="bg-gray-100 rounded-md p-1 shadow-inner text-xs text-gray-800 break-words">
                  <h3 className="text-gray-700 font-semibold mb-2">Transaction Response</h3>
                  <div className="space-y-1 text-sm">
                    {(() => {
                      try {
                        const parsed = JSON.parse(selectedReport?.transaction_response);
                        const meta = parsed?.initiateAuthGenericFundTransferAPIResp?.metaData;
                        return meta ? (
                          <>
                            <div>
                              <span className="font-semibold text-gray-600">Status:</span>{' '}
                              <span className="text-gray-800">{meta?.status || '-'}</span>
                            </div>
                            <div>
                              <span className="font-semibold text-gray-600">Message:</span>{' '}
                              <span className="text-gray-800">{meta?.message || '-'}</span>
                            </div>

                          </>
                        ) : (
                          <p className="text-gray-500 italic">Meta data not available</p>
                        );
                      } catch (e) {
                        return <pre className="text-gray-600">{selectedReport?.transaction_response}</pre>;
                      }
                    })()}
                  </div>

                </div>

              </>
            ) : (
              <p className="text-center text-gray-500">No Data Found</p>
            )}
          </div>

          <div className='flex justify-center gap-2 mt-2'>
            <button
              onClick={handleDownload}
              className=" right-2 bg-blue-600 hover:bg-blue-800 text-white text-sm p-1 rounded-md shadow-md"
            >
              <IoMdDownload size={16} className='inline' /> Download
            </button>

            <button
              onClick={() => setIsOpen(false)}
              className="left-2 bg-red-500 hover:bg-red-700 text-white text-xs font-semibold px-3 py-1 rounded-md shadow"
            >
              ✕ Close
            </button>
          </div>
        </div>
      </DataModal>

      <Seo />
      <DivTag className=" mt-8 bg-white mb-8">
        <ReportHeading name="Payout Report" />

        <div className="grid grid-cols-1 md:grid-cols-4 gap-1 mb-2 mt-5 px-4">
          <input name="account_number" value={filters.account_number} onChange={handleFilterChange} placeholder="Account Number" className="border rounded-md p-1 border-blue-600" />
          <input name="beneficiary_name" value={filters.beneficiary_name} onChange={handleFilterChange} placeholder="Beneficiary Name" className="border rounded-md p-1 border-blue-600" />
          <input name="user_id" value={filters.user_id} onChange={handleFilterChange} placeholder="User Id" className="border rounded-md p-1 border-blue-600 " type="text" />
          <input name="amount" value={filters.amount} onChange={handleFilterChange} placeholder="Amount" className="border rounded-md p-1 border-blue-600 " type="number" />
          <input name="from_date" value={filters.from_date} onChange={handleFilterChange} type="date" className="border rounded-md p-1 border-blue-600" />
          <input name="to_date" value={filters.to_date} onChange={handleFilterChange} type="date" className="border mt-2 rounded-md p-1 border-blue-600" />
          <select name="txn_type" value={filters.txn_type} onChange={handleFilterChange} className="border mt-2 rounded-md p-1 border-blue-600">
            <option value="">All</option>
            <option value="NEFT">NEFT</option>
            <option value="IMPS">IMPS</option>
            <option value="RTGS">RTGS</option>
          </select>
        </div>

        <div className='flex justify-center gap-4'>
          <button onClick={handleSearch} className="bg-blue-600 text-white px-2 py-1 rounded mb-2 mt-2 w-1/7">
            Search
          </button>
          <button onClick={handleReset} className="bg-red-600 text-white px-2 py-1 rounded mt-2 mb-2 w-1/7">
            Reset
          </button>
        </div>

        {/* Desktop Table */}
        <div className="overflow-x-auto mt-2 hidden md:block">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-blue-800 text-white text-md">
              <tr className="font-thin">
                <th className="p-1 border">SN.</th>
                <th className="p-1 border">User Id</th>
                <th className="p-1 border">Beneficiary</th>
                <th className="p-1 border">Order ID</th>
                <th className="p-1 border">From_A/C</th>
                <th className="p-1 border">To A/C</th>
                <th className="p-1 border">Balance</th>
                <th className="p-1 border">RRN/UTR</th>
                <th className="p-1 border">Date</th>
                <th className="p-1 border">Mode</th>
                <th className="p-1 border">Type</th>
                <th className="p-1 border">Amount</th>
                <th className="p-1 border">Status</th>
                <th className="p-1 border">Action</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {payouts.length > 0 ? (
                payouts.map((item, idx) => (
                  <tr key={idx} className="border hover:bg-blue-50 text-center">
                    <td className="p-1 border shadow-inner shadow-blue-600 font-bold">
                      {(currentPage - 1) * 10 + idx + 1}
                    </td>
                    <td className="p-1 border shadow-inner shadow-blue-400">{item?.user?.user_id || "-"}</td>
                    <td className="p-1 border shadow-inner shadow-blue-400 text-xs">{item?.beneficiary_name || "-"}</td>
                    <td className="p-1 border shadow-inner shadow-blue-400 text-xs">{item?.transaction_id || "-"}</td>
                    <td className="p-1 border shadow-inner shadow-blue-400">-</td>
                    <td className="p-1 border shadow-inner shadow-blue-400">{item?.credit_account_number || "-"}</td>

                    <td className="p-1 border shadow-inner shadow-blue-400">{item?.balance || "-"}</td>
                    <td className="p-1 border shadow-inner shadow-blue-400 text-xs">{item?.utr || "-"}</td>
                    <td className="p-1 border text-xs shadow-inner shadow-blue-400">
                      {moment(item?.created_at).format("DD/MM/YYYY h:mmA")}
                    </td>
                    <td className="p-1 border shadow-inner shadow-blue-400">{item?.payout_mode?.name || "-"}</td>
                    <td className="p-1 border shadow-inner shadow-blue-400">{item?.factor || "-"}</td>
                    <td className="p-1 border shadow-inner shadow-blue-400">₹{item?.amount || "-"}</td>
                    <td className="p-1 border shadow-inner shadow-blue-400 text-center">
                      {item?.status.value === "pending" ? (
                        <select
                          className="w-full px-1 py-0 text-sm rounded-full border bg-yellow-500 text-white font-medium cursor-pointer"
                          defaultValue={item?.status.value}
                          onChange={async (e) => {
                            const newStatus = e.target.value;
                            if (newStatus && newStatus !== "pending") {
                              const res = await UpdateTxn({
                                id: item?.id,
                                new_status: newStatus,
                              });

                              if (res?.data || res?.data?.data) {
                                showSuccessToast("Status updated successfully!");
                                fetchPayouts(); // 🔄 refresh payouts after update
                              } else {
                                showErrorToast(res?.message || "Failed to update status");
                              }
                            }
                          }}
                        >
                          <option value="pending">Pending</option>
                          <option value="success">Success</option>
                          <option value="failed">Failed</option>
                        </select>
                      ) : (
                        <button
                          className={`w-full px-1 py-0 text-white font-medium text-sm rounded-full cursor-default 
        ${item?.status.value === "success"
                              ? "bg-green-500"
                              : item?.status.value === "failed"
                                ? "bg-red-500"
                                : "bg-gray-400"
                            }`}
                          disabled
                        >
                          {item?.status.value || "-"}
                        </button>
                      )}
                    </td>


                    <td className="p-1 border shadow-inner shadow-blue-400">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedReport(item);
                            setIsOpen(true);
                          }}
                          className="text-xs bg-green-600 text-white px-1 py-0.5 rounded hover:bg-green-700 transition"
                        >
                          View
                        </button>

                        <button
                          type="button"
                          onClick={() => handleTxnStatus(item.id)}
                          className="text-xs bg-blue-500 text-white px-1 py-0.5 rounded hover:bg-blue-700 transition"
                        >
                          TxnStatus
                        </button>
                      </div>
                    </td>

                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={13} className="text-center text-gray-500 py-2">
                    No Data Found
                  </td>
                </tr>
              )}
            </tbody>

            {isModalOpens && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                <div className="bg-white rounded-xl shadow-lg w-full max-w-lg mx-4 relative">
                  {/* Header */}
                  <div className="flex items-center justify-between border-b px-4 py-2">
                    <h2 className="text-lg font-semibold text-gray-800">
                      Transaction Response
                    </h2>
                    <button
                      onClick={() => setIsModalOpens(false)}
                      className="text-gray-500 hover:text-gray-800 transition"
                    >
                      ✕
                    </button>
                  </div>

                  {/* Body */}
                  <div className="p-4 max-h-80 overflow-auto">
                    <pre className="text-xs bg-gray-100 p-3 rounded-lg text-gray-700">
                      {JSON.stringify(txnResponse, null, 2)}
                    </pre>
                  </div>

                  {/* Footer */}
                  <div className="flex justify-end border-t px-4 py-2">
                    <button
                      onClick={() => setIsModalOpens(false)}
                      className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}

          </table>
        </div>

        {/* Mobile Card View */}
        <div className="block md:hidden mt-2 grid-cols-1 sm:grid-cols-2 gap-1">
          {payouts.length > 0 ? (
            payouts.map((item, idx) => (
              <div key={idx} className="border rounded-lg shadow p-2 bg-white">
                <div className="flex justify-between mb-2">
                  <span className="font-bold">SN: {(currentPage - 1) * 10 + idx + 1}</span>
                  <span
                    className={`px-2 py-0.5 text-white text-xs rounded-full 
              ${item?.status.value === "success"
                        ? "bg-green-500"
                        : item?.status.value === "pending"
                          ? "bg-yellow-500"
                          : "bg-red-500"
                      }`}
                  >
                    {item?.status.value || "-"}
                  </span>
                </div>
                <p><strong>Order ID:</strong> {item?.transaction_id || "-"}</p>
                <p><strong>To A/C:</strong> {item?.credit_account_number || "-"}</p>
                <p><strong>Beneficiary:</strong> {item?.beneficiary_name || "-"}</p>
                <p><strong>Amount:</strong> ₹{item.amount || "-"}</p>
                <p><strong>Date:</strong> {moment(item?.created_at).format("DD/MM/YYYY h:mmA")}</p>
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedReport(item);
                      setIsOpen(true);
                    }}
                    className="text-xs bg-green-600 text-white px-2 py-1 rounded hover:bg-blue-900"
                  >
                    View
                  </button>
                </div>
              </div>
            ))
          ) : (
            <p className="text-center text-gray-500 col-span-2">No Data Found</p>
          )}
        </div>
        <PaginationData currentPage={currentPage} lastPage={lastPage} handlePage={handlePage} />
      </DivTag>
    </motion.div>
  );
};
export default PayoutReport;













